import { templateIdForServer } from '@electron/shared/mcp-template-gating';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { electronApis } from '@/features/electron-api';
import { integrationKeys } from '@/features/integrations/integrations.query';
import { logger } from '@/lib/logger';
import { inBrowser } from '@/utils/capabilities';

import type {
  ConnectionTestResult,
  ConnectionTestStamp,
  ServerEntry,
  ServerEntryInput,
  ServerTransport,
} from '@littlebird/axon-mcp';

export const AXON_MCP_SERVERS_QUERY_KEY = ['axon-mcp-servers'] as const;

export const useMcpServersQuery = (enabled = true) =>
  useQuery({
    queryKey: AXON_MCP_SERVERS_QUERY_KEY,
    queryFn: () => electronApis.axonMcp.getServers(),
    enabled: enabled && !inBrowser(),
  });

export const useReservedSlugsQuery = () =>
  useQuery({
    queryKey: ['axon-mcp-reserved-slugs'],
    queryFn: () => electronApis.axonMcp.getReservedSlugs(),
    enabled: !inBrowser(),
    staleTime: Infinity,
  });

/** First saved folder the local-folder servers cannot open, if any. */
export const useLocalPathCheckQuery = (
  serverSlug: string | undefined,
  paths: readonly string[],
  serversUpdatedAt: number
) =>
  useQuery({
    // Keyed on when the server list last changed, never on the paths: query
    // keys must stay free of local paths (see configuration-privacy.test.tsx)
    queryKey: ['axon-mcp-local-path-check', serverSlug, serversUpdatedAt],
    queryFn: () => electronApis.axonMcp.checkLocalPaths(paths),
    enabled: !inBrowser() && serverSlug !== undefined && paths.length > 0,
  });

// Counts each server's transport changes, so the query key changes with a
// secret without holding it. Only the current transport is kept, and a
// removed server's entry goes with it.
const transportRevisions = new Map<
  string,
  { transport: string; revision: number }
>();
function transportRevision(slug: string, transport: ServerTransport): number {
  const serialized = JSON.stringify(transport);
  const current = transportRevisions.get(slug);
  if (current?.transport === serialized) return current.revision;
  const revision = (current?.revision ?? 0) + 1;
  transportRevisions.set(slug, { transport: serialized, revision });
  return revision;
}

/**
 * Live connection test of a saved server, run when the panel mounts, when its
 * transport changes (including back to an earlier one), or on refetch. A failed
 * test re-downloads the package, so window focus and reconnects must not
 * trigger one.
 */
export const useServerConnectionTestQuery = (server: ServerEntry) => {
  const queryClient = useQueryClient();
  return useQuery({
    // Never keyed on the transport itself, which can hold secrets
    queryKey: [
      'axon-mcp-connection-test',
      server.slug,
      transportRevision(server.slug, server.transport),
    ],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const result = await electronApis.axonMcp.testServer(
        server.transport,
        server.name,
        templateIdForServer(server)
      );
      try {
        await saveConnectionTestStamp(queryClient, server, result);
      } catch (error) {
        // The live result still shows; only the integration list's stamp is stale
        logger.error('Could not save a connection test result', {
          slug: server.slug,
          error,
        });
      }
      return result;
    },
    enabled: !inBrowser(),
  });
};

// The integration list reads the saved stamp, so it must follow the live
// result; skip it if the transport changed while the test ran
async function saveConnectionTestStamp(
  queryClient: QueryClient,
  server: ServerEntry,
  result: ConnectionTestResult
) {
  const saved = (await electronApis.axonMcp.getServers()).find(
    (entry) => entry.slug === server.slug
  );
  if (
    !saved ||
    JSON.stringify(saved.transport) !== JSON.stringify(server.transport) ||
    saved.lastConnectionTest?.ok === result.ok
  ) {
    return;
  }
  queryClient.setQueryData(
    AXON_MCP_SERVERS_QUERY_KEY,
    await electronApis.axonMcp.updateServer(server.slug, {
      lastConnectionTest: toConnectionTestStamp(result),
    })
  );
}

export const useAddMcpServerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ServerEntryInput) =>
      electronApis.axonMcp.addServer(input),
    onSuccess: (servers) =>
      queryClient.setQueryData(AXON_MCP_SERVERS_QUERY_KEY, servers),
    onError: () => toast.error('Failed to add local MCP server'),
  });
};

function rememberOnDeviceRemoval(
  queryClient: QueryClient,
  server: ServerEntry | undefined
): void {
  const template = server && templateIdForServer(server);
  if (!template) return;
  backendApis.integrations
    .markIntegrationSuggestionRemoved(template)
    // The cached list can still hold the suggestion this server once hid
    .then(() =>
      queryClient.invalidateQueries({ queryKey: integrationKeys.suggestions() })
    )
    .catch((error) => {
      logger.warn('Integration suggestion removal not recorded', { error });
    });
}

const cachedServer = (queryClient: QueryClient, slug: string) =>
  queryClient
    .getQueryData<ServerEntry[]>(AXON_MCP_SERVERS_QUERY_KEY)
    ?.find((server) => server.slug === slug);

export const useUpdateMcpServerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      slug,
      changes,
    }: {
      slug: string;
      changes: Partial<ServerEntryInput> & { enabled?: boolean };
    }) => electronApis.axonMcp.updateServer(slug, changes),
    onMutate: ({ slug }) => ({ server: cachedServer(queryClient, slug) }),
    onSuccess: (servers, { changes }, context) => {
      if (changes.enabled === false)
        rememberOnDeviceRemoval(queryClient, context?.server);
      queryClient.setQueryData(AXON_MCP_SERVERS_QUERY_KEY, servers);
    },
    onError: () => toast.error('Failed to update local MCP server'),
  });
};

export const useRemoveMcpServerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => electronApis.axonMcp.removeServer(slug),
    onMutate: (slug) => ({ server: cachedServer(queryClient, slug) }),
    onSuccess: (servers, slug, context) => {
      rememberOnDeviceRemoval(queryClient, context?.server);
      transportRevisions.delete(slug);
      queryClient.setQueryData(AXON_MCP_SERVERS_QUERY_KEY, servers);
    },
    onError: () => toast.error('Failed to remove local MCP server'),
  });
};

export const useTestMcpServerMutation = () =>
  useMutation({
    mutationFn: ({
      transport,
      name,
      templateId,
    }: {
      transport: ServerTransport;
      name?: string;
      templateId?: string;
    }) => electronApis.axonMcp.testServer(transport, name, templateId),
    onError: () => toast.error('Failed to test local MCP server'),
  });

function toConnectionTestStamp(
  result: ConnectionTestResult
): ConnectionTestStamp {
  return {
    ok: result.ok,
    serverName: result.serverName,
    toolCount: result.toolCount,
    testedAt: new Date().toISOString(),
  };
}

/** Re-test a server and persist the result as its `lastConnectionTest`, returning the fresh list. */
const refreshServerConnectionTest = async (
  server: ServerEntry
): Promise<ServerEntry[]> => {
  let lastConnectionTest: ConnectionTestStamp;
  try {
    lastConnectionTest = toConnectionTestStamp(
      await electronApis.axonMcp.testServer(
        server.transport,
        server.name,
        templateIdForServer(server)
      )
    );
  } catch {
    lastConnectionTest = { ok: false, testedAt: new Date().toISOString() };
  }
  return electronApis.axonMcp.updateServer(server.slug, {
    lastConnectionTest,
  });
};

export const useRefreshMcpServerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (server: ServerEntry) => refreshServerConnectionTest(server),
    onSuccess: (servers) =>
      queryClient.setQueryData(AXON_MCP_SERVERS_QUERY_KEY, servers),
    onError: () => toast.error('Failed to refresh local MCP server'),
  });
};
